import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { BrandColourModel } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['brandColours']

type BrandColourFilters = {
  brand_id?: number,
}

type UseBrandColoursOptions = {
  entityKey?: string,
  filters?: BrandColourFilters,
  performHttpRequests?: boolean,
}

function useBrandColours(options: UseBrandColoursOptions = {}) {
  const { entityKey, filters = {} } = options
  const {
    brand_id: filterBrandId,
  } = filters

  const {
    updatedEntities: { brandColours: brandColoursUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { brandColours } = useSelector(reduxState => reduxState.entities)

  const filteredBrandColours = useMemo((): BrandColourModel[] => {
    const filtered = Object.values(brandColours).filter((brandColour) => {
      const { brand_id } = brandColour

      const matchBrand = matchFilterNumber(brand_id, filterBrandId)
      return matchBrand
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [brandColoursUpdatedAt, JSON.stringify(options)])

  const filteredBrandColoursCount = filteredBrandColours.length
  const hasFilteredBrandColours = !!filteredBrandColoursCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandColoursCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrandColours } = useReduxAction(
    'brandColours',
    'loadBrandColours',
    {
      entityKey,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandColours,
    filteredBrandColoursCount,
    hasFilteredBrandColours,
    loading: loadingBrandColours,
  }
}

export default useBrandColours
